import { MonoTypeOperatorFunction } from 'rxjs';
import { distinctUntilChanged } from 'rxjs/operators';

import { ComponentTypeRef } from './../../ng-api';
import { anyToString, cloneDeep, deepEquals, freezeDeep, isFunction, isNotNil, isString, perfMeasure } from './../../utils';

/* Copyright IBM Corp. 2017 */
export const KEY_DEBUG = '$$DEBUG';

const _assign = Object.assign;
const _defineProperties = Object.defineProperties;

/*
 * shortcut for true
 */
const _true = true;

/*
 * shortcut for false
 */
const _false = false;

/*
 * Access the keys
 */
const _keys = Object.keys;

/*
 * Performs a deep clone for the value, supports just plain objects and arrays
 *
 * @param aValue
 */
function _perfCloneDeep(aValue: any): any {
  // measure
  const handle = perfMeasure('_perfCloneDeep');
  // identity check
  const res = cloneDeep(aValue);
  // done
  handle();
  // done
  return res;
}

/*
 * Performs a deep freeze for the value, supports just plain objects and arrays
 *
 * @param aValue
 */
function _perfFreezeDeep(aValue: any): any {
  // measure
  const handle = perfMeasure('_perfFreezeDeep');
  // identity check
  const res = freezeDeep(aValue);
  // done
  handle();
  // done
  return res;
}

/*
 * Tests if two objects are identical
 *
 * @param aLeft     left object
 * @param aRight    right object
 */
function _perfDeepEquals(aLeft: any, aRight: any): boolean {
  // measure
  const handle = perfMeasure('_perfDeepEquals');
  // identity check
  const res = deepEquals(aLeft, aRight);
  // done
  handle();
  // done
  return res;
}

/*
 * Appends the arguments to the target array
 *
 * @param aDst  target array
 * @param aArgs   the arguments
 * @return the array
 */
function _concatArguments(aDst: any[], aArgs: IArguments): any[] {
  let argsLen = aArgs.length;
  const dstLen = aDst.length;
  while (argsLen-- > 0) {
    aDst[argsLen + dstLen] = aArgs[argsLen];
  }
  return aDst;
}

const _getPrototype = Object.getPrototypeOf;

/**
 * Tries to decode the classname
 *
 * @param aThis     the instance pointer
 * @param aDefault  default name
 */
function _getClassName(aThis: any, aDefault?: string): string {
  if (isFunction(_getPrototype)) {
    const pt = _getPrototype(aThis);
    if (isNotNil(pt)) {
      const cst = pt.constructor;
      if (isNotNil(cst)) {
        const name = cst.name;
        if (isString(name)) {
          return name;
        }
      }
    }
  }
  // default
  return aDefault || anyToString(aThis);
}

function _isEqualComponentTypeRef(
  aLeft: ComponentTypeRef<any>,
  aRight: ComponentTypeRef<any>
): boolean {
  return (
    aLeft === aRight ||
    (aLeft.type === aRight.type && aLeft.resolver === aRight.resolver)
  );
}

export const opDistinctComponentTypeRef: MonoTypeOperatorFunction<
  ComponentTypeRef<any>
> = distinctUntilChanged(_isEqualComponentTypeRef);

// export the private functions
export {
  _keys as keys,
  _perfDeepEquals as deepEquals,
  _perfCloneDeep as cloneDeep,
  _concatArguments as concatArgs,
  _defineProperties as defineProperties,
  _isEqualComponentTypeRef as isEqualComponentTypeRef,
  _assign as assign,
  _getClassName as getClassName
};
